import {useCallback, useContext, useEffect, useRef} from "react";

import {algod} from "../../../core/integrations/algosdk/AlgodManager";
import {fetchAccountAssetDetails, isPeraWallet} from "../walletUtils";
import WalletContext, {WalletContextDispatch} from "./WalletContext";
import {
  DEFAULT_DELAY_IN_FETCHING_ACCOUNT_DATA,
  MAX_NUMBER_OF_ATTEMPTS_TO_FETCH_ACCOUNT_DATA
} from "./WalletContext.constants";
import {WalletContextReducerAction} from "./WalletContext.types";
import {AppStateAccountData} from "../../account/accountTypes";
import usePreviousValue from "../../../core/util/hook/usePrevious";
import {sleep} from "../../../util/promise/promiseUtils";
import {WalletType} from "../walletTypes";

function useWalletContext() {
  const value = useContext(WalletContext);

  if (!value) {
    throw new Error("Wallet context used outside provider");
  }

  return value;
}

function useWalletContextDispatch() {
  return useContext(WalletContextDispatch);
}

function useSelectedAccount() {
  return useWalletContext().account;
}

function useSelectedAccountAddress() {
  return useWalletContext().account?.address || null;
}

function useSelectedAccountNFDomain() {
  return useWalletContext().account?.nfdomain || null;
}

function useSelectedAccountAssets() {
  return useWalletContext().accountAssets;
}

function useSelectedWalletType() {
  const {walletType} = useWalletContext();

  return {walletType, isPeraWallet: isPeraWallet(walletType)};
}

function useSelectedWalletAccounts() {
  return useWalletContext().walletAccounts;
}

/**
 * Runs the callback passed when selectedAccount's address changes (ie. on account disconnect or when switched to using another account)
 */
function useOnSelectedAccountAddressChange(callback: VoidFunction) {
  const currentAccountAddress = useSelectedAccountAddress();
  const previousAccountAddress = usePreviousValue(currentAccountAddress);
  const callbackRef = useRef(callback);

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (currentAccountAddress !== previousAccountAddress) {
      callbackRef.current();
    }
  }, [currentAccountAddress, previousAccountAddress]);
}

/**
 * @returns `updateSelectedAccountState` function that can be used to update the selected account state
 */
function useUpdateSelectedAccountState() {
  const selectedAccountAddress = useSelectedAccountAddress();
  const walletContextDispatch = useWalletContextDispatch();

  const updateSelectedAccountState = useCallback(() => {
    return fetchAndSetAccountData(walletContextDispatch, selectedAccountAddress);
  }, [selectedAccountAddress, walletContextDispatch]);

  return {
    /**
     * Fetches account information for the current selected account,
     * and updates the account state in the wallet context.
     */
    updateSelectedAccountState
  };
}

/**
 * Fetches account information for `accountAddress` and saves the new data within appState.
 *
 * @param dispatch - appState reducer's dispatch
 * @param accountAddress - Address of an account
 * @param delayFor - Delays the operation if delayFor is greater than 0
 */
function fetchAndSetAccountData(
  dispatch: React.Dispatch<WalletContextReducerAction>,
  accountAddress: null | undefined | string,

  // Default delay is introduced because we use this to fetch new account data after a certain async process in Tinyman, eg. after swap or redeem.
  // This small delay is introduced before fetching the data so that it is more likely the changes are reflected in the account data
  delayFor = DEFAULT_DELAY_IN_FETCHING_ACCOUNT_DATA
) {
  return new Promise<AppStateAccountData>(async (resolve, reject) => {
    if (accountAddress) {
      // try to fetch account information for MAX_NUMBER_OF_ATTEMPTS_TO_FETCH_ACCOUNT_DATA times
      // if all attempts failes, then reject the process
      let attempt = 1;

      while (attempt <= MAX_NUMBER_OF_ATTEMPTS_TO_FETCH_ACCOUNT_DATA) {
        try {
          if (attempt > 1 || delayFor > 0) {
            await sleep(delayFor || DEFAULT_DELAY_IN_FETCHING_ACCOUNT_DATA);
          }

          const accountInfo = await algod.client.accountInformation(accountAddress).do();
          const accountAssets = await fetchAccountAssetDetails(accountInfo);
          const accountDetail: AppStateAccountData = {
            ...accountInfo,
            // todo
            nfdomain: undefined
          };

          dispatch({
            type: "SET_ACCOUNT",
            account: {
              detail: accountDetail,
              assets: accountAssets
            }
          });

          resolve(accountDetail);
          break;
        } catch (error) {
          attempt += 1;
        }
      }

      if (attempt > MAX_NUMBER_OF_ATTEMPTS_TO_FETCH_ACCOUNT_DATA) {
        reject(new Error("Failed to fetch account information."));
      }
    } else {
      reject(new Error("Failed to fetch account information. No address was provided."));
    }
  });
}

/**
 * @returns true if the passed wallet type is a parent signer
 */
function getIsParentSigner(walletType: WalletType | null) {
  return Boolean(walletType && walletType === WalletType.ParentSigner);
}

export {
  useWalletContext,
  useWalletContextDispatch,
  useSelectedAccount,
  useSelectedAccountAddress,
  useSelectedAccountNFDomain,
  useSelectedAccountAssets,
  useSelectedWalletType,
  useSelectedWalletAccounts,
  fetchAndSetAccountData,
  useUpdateSelectedAccountState,
  useOnSelectedAccountAddressChange,
  getIsParentSigner
};
